import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, FileText, Send } from 'lucide-react';
import type { PurchaseOrder } from '@/lib/types/job';

interface PurchaseOrderCardProps {
  purchaseOrder: PurchaseOrder;
  onView: (poId: string) => void;
  onExport?: (poId: string) => void;
  onSend?: (poId: string) => void;
}

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'draft':
      return 'bg-muted text-muted-foreground';
    case 'sent':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'acknowledged':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'modification_requested':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    case 'completed':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
    case 'closed':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

export function PurchaseOrderCard({ 
  purchaseOrder, 
  onView, 
  onExport, 
  onSend 
}: PurchaseOrderCardProps) {
  const formatCurrency = (cents: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg font-medium">
              {purchaseOrder.po_number}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Job: {(purchaseOrder as any).jobs?.title || 'N/A'}
            </p>
          </div>
          <Badge className={getStatusColor(purchaseOrder.status)}>
            {purchaseOrder.status.replace('_', ' ')}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Amount:</span>
            <p className="font-medium">
              {formatCurrency(purchaseOrder.po_amount_cents, purchaseOrder.currency)}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Created:</span>
            <p className="font-medium">{formatDate(purchaseOrder.created_at)}</p>
          </div>
        </div>

        {purchaseOrder.vendor_acknowledged_at && (
          <div className="text-sm">
            <span className="text-muted-foreground">Acknowledged:</span>
            <p className="font-medium">{formatDate(purchaseOrder.vendor_acknowledged_at)}</p>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView(purchaseOrder.id)}
            className="flex-1"
          >
            <Eye className="h-4 w-4 mr-2" />
            View
          </Button>
          
          {onExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport(purchaseOrder.id)}
            >
              <FileText className="h-4 w-4 mr-2" />
              Export
            </Button>
          )}
          
          {onSend && purchaseOrder.status === 'draft' && (
            <Button
              variant="default"
              size="sm"
              onClick={() => onSend(purchaseOrder.id)}
            >
              <Send className="h-4 w-4 mr-2" />
              Send
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}